import { toast } from 'react-toastify';

/**
 * How urgent a message is, and how it reads. Kept as a domain vocabulary
 * rather than the toast library's own, because the delivery mechanism is
 * expected to change (issue #1141) and the levels are not.
 */
export type MessageLevel = 'success' | 'error' | 'warn' | 'info';

/**
 * Identifies the *event* a message is about, so repeats of that event are
 * recognised as the same thing instead of piling up. Two messages sharing a
 * key are one message.
 */
export type MessageKey = string;

export interface NotifyOptions {
  readonly key?: MessageKey;
}

export interface Notifier {
  readonly success: (message: string, options?: NotifyOptions) => void;
  readonly error: (message: string, options?: NotifyOptions) => void;
  readonly warn: (message: string, options?: NotifyOptions) => void;
  readonly info: (message: string, options?: NotifyOptions) => void;
  /** Whether a message with this key is on screen right now. */
  readonly isShowing: (key: MessageKey) => boolean;
}

// NOTE: omit the options object entirely when there is no key, rather than
// passing `{ toastId: undefined }` — the library treats a present-but-
// undefined id as an explicit "no id", which is the same thing here, but the
// distinction is not documented and is not worth depending on.
const toastOptions = (options?: NotifyOptions) => (options?.key === undefined ? undefined : { toastId: options.key });

/**
 * The one way to tell the user something. Everything user-facing goes through
 * here rather than calling `react-toastify` directly, and a lint rule keeps it
 * that way (`no-restricted-imports` on `react-toastify` outside this folder).
 *
 * The point of the indirection is that a toast is the wrong medium for a
 * message you might want to read twice: it fades, it cannot be recalled, and
 * two of them arriving together are unreadable in the order they arrived. A
 * message centre replaces the implementation behind this façade (issue #1141)
 * without touching a single call site.
 *
 * That also makes this the boundary where wording lives. The API layer reports
 * machine-readable problems and never authors copy, because an endpoint does
 * not know whether it is serving a dialog or a background poller (issue #1035).
 */
export const notify: Notifier = {
  success: (message, options) => {
    toast.success(message, toastOptions(options));
  },
  error: (message, options) => {
    toast.error(message, toastOptions(options));
  },
  warn: (message, options) => {
    toast.warn(message, toastOptions(options));
  },
  info: (message, options) => {
    toast.info(message, toastOptions(options));
  },
  isShowing: (key) => toast.isActive(key),
};
